import React, { useEffect, useRef, useState } from "react";
import MailboxSticker from "./MailboxSticker";
import ChatBoltSticker from "./ChatBoltSticker";

const LINKEDIN_URL = "https://www.linkedin.com/in/kuhuk/";

// Keep in sync with the limits enforced in netlify/functions/contact.js.
const MAX_NAME_LENGTH = 100;
const MAX_EMAIL_LENGTH = 254; // the maximum length of a valid email address (RFC 5321)
const MAX_MESSAGE_LENGTH = 5000;

// Draws the input outline as a fieldset so the "n / max" counter sits on the border line
// (as a legend), like a label notched into the outline.
const LimitedField = ({ length, max, children }) => (
  <fieldset className="contact-input-wrap">
    <legend className={`contact-char-counter${length >= max ? " contact-char-counter-limit" : ""}`}>
      {length} / {max}
    </legend>
    {children}
  </fieldset>
);

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ContactMe = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — humans never see or fill this
  const formLoadedAt = useRef(Date.now());
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [statusMessage, setStatusMessage] = useState("");

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://manage.instamojo.com/assets/js/pay_button/button.min.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const validate = () => {
    const nextErrors = {};
    if (!name.trim()) nextErrors.name = "Please enter your name.";
    if (!email.trim()) nextErrors.email = "Please enter your email.";
    else if (!emailPattern.test(email.trim())) nextErrors.email = "Please enter a valid email address.";
    if (!message.trim()) nextErrors.message = "Please enter a message.";
    return nextErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setStatus("idle");
      setStatusMessage("");
      return;
    }

    setStatus("sending");
    setStatusMessage("");

    try {
      const response = await fetch("/.netlify/functions/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          message,
          website,
          elapsedMs: Date.now() - formLoadedAt.current,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Something went wrong. Please try again.");
      }

      setStatus("success");
      setStatusMessage("Letter posted ✓ — on its way, I'll write back soon.");
      setName("");
      setEmail("");
      setMessage("");
      setErrors({});
    } catch (err) {
      setStatus("error");
      setStatusMessage(err.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="div-contact-form">

      {/* LINKEDIN CTA */}
      <div className="contact-card linkedin-card">
        <ChatBoltSticker className="chat-bolt-sticker" />
        <p className="section-title contact-title">let's connect 🔗</p>
        <p className="linkedin-subtitle">I reply fastest on LinkedIn.</p>
        <a
          href={LINKEDIN_URL}
          target="_blank"
          rel="noreferrer"
          className="pill-button pill-button-primary"
        >
          Connect on LinkedIn →
        </a>
      </div>

      {/* CONTACT FORM */}
      <div className="contact-card-wrap">
        <MailboxSticker className="mailbox-sticker" />
        <div className="contact-card">
        <p className="section-title contact-title">or send me a letter 💌</p>

        <form
          id="contact-form"
          className="contact-form"
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="contact-field">
            <LimitedField length={name.length} max={MAX_NAME_LENGTH}>
              <input
                name="name"
                className={`contact-input${errors.name ? " contact-input-invalid" : ""}`}
                type="text"
                placeholder="Name"
                maxLength={MAX_NAME_LENGTH}
                value={name}
                onChange={(e) => setName(e.target.value)}
                aria-invalid={Boolean(errors.name)}
              />
            </LimitedField>
            {errors.name && <p className="contact-field-error">{errors.name}</p>}
          </div>

          <div className="contact-field">
            <LimitedField length={email.length} max={MAX_EMAIL_LENGTH}>
              <input
                name="email"
                className={`contact-input${errors.email ? " contact-input-invalid" : ""}`}
                type="email"
                placeholder="Email"
                maxLength={MAX_EMAIL_LENGTH}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={Boolean(errors.email)}
              />
            </LimitedField>
            {errors.email && <p className="contact-field-error">{errors.email}</p>}
          </div>

          <div className="contact-field">
            <LimitedField length={message.length} max={MAX_MESSAGE_LENGTH}>
              <textarea
                name="message"
                className={`contact-input contact-textarea${errors.message ? " contact-input-invalid" : ""}`}
                placeholder="Message"
                maxLength={MAX_MESSAGE_LENGTH}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                aria-invalid={Boolean(errors.message)}
              />
            </LimitedField>
            {errors.message && <p className="contact-field-error">{errors.message}</p>}
          </div>

          <input
            name="website"
            className="contact-hp"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />

          <button
            type="submit"
            className="pill-button pill-button-primary send-button"
            disabled={status === "sending"}
          >
            {status === "sending" ? "Posting..." : "Post Letter"}
          </button>

          {status === "success" && <p className="contact-status contact-status-success">{statusMessage}</p>}
          {status === "error" && <p className="contact-status contact-status-error">{statusMessage}</p>}
        </form>
        </div>
      </div>

    </div>
  );
};

export default ContactMe;
